<template>
	<div class="app-wrapper">
		<!-- 顶部信息栏 -->
		<header class="header">
			<div class="header-left">
				<div class="school-badge">
					<span class="badge-mark">AI</span>
					<span class="badge-copy">
						<span class="badge-eyebrow">实时训练</span>
						<span class="badge-text">{{ schoolName || '驾校名称' }}</span>
					</span>
				</div>
			</div>
			<div class="header-center">
				<div class="car-plate">
					<span class="plate-dot"></span>
					{{ CarNum || '车牌号' }}
				</div>
			</div>
			<div class="header-right">
				<div class="student-info">
					<span class="student-name">{{ stuInfo.studentName || '学员' }}</span>
					<span class="car-type-badge">{{ stuInfo.carType == 1 ? 'C1' : 'C2' }}</span>
				</div>
			</div>
		</header>

		<!-- 地图区域 -->
		<section class="map-section">
			<div class="map-toolbar">
				<div>
					<div class="section-kicker">场地实时轨迹</div>
					<div class="section-caption"><span class="live-dot"></span>车辆位置持续同步</div>
				</div>
				<div class="map-controls" role="group" aria-label="地图缩放控制">
					<button type="button" aria-label="缩小地图" @click="zoomOut">−</button>
					<button type="button" class="zoom-reset" aria-label="重置地图缩放" @click="scale = 10; drawScene()">100%</button>
					<button type="button" aria-label="放大地图" @click="zoomIn">＋</button>
				</div>
			</div>
			<div class="canvas-wrapper">
				<canvas ref="trackCanvas" class="track-canvas">
				</canvas>
				<div class="map-label">LIVE · GPS</div>
			</div>
			<div class="speed-display" :class="{ 'warning': speed > 60 }">
				<div class="speed-ring"><div class="speed-value">{{ formatSpeed(speed) }}</div><div class="speed-unit">km/h</div></div>
				<div class="speed-caption">当前速度</div>
			</div>
		</section>

		<!-- 视频播放器区域 -->
		<section class="player-section">
			<div class="player-heading">
				<div>
					<div class="section-kicker">车内视频</div>
					<div class="section-caption">实时画面 · 仅供训练复盘</div>
				</div>
				<span class="video-status"><span class="live-dot"></span>实时</span>
			</div>
			<div class="player-body"><Player /></div>
		</section>

		<!-- 实时状态栏 -->
		<footer class="footer">
			<div class="status-title">
				<div><span class="section-kicker">车辆状态</span><span class="section-caption">实时信号</span></div>
				<span class="status-pulse"><span class="live-dot"></span>数据正常</span>
			</div>

			<div class="status-panel">
				<div class="status-indicators">
					<div class="indicator-item" :class="{ 'active': left == 1 }">
						<div class="indicator-icon">
							<img class="icon" src="@/assets/im_icon_09.png" alt="左转" v-if="left == 1" />
							<img class="icon" src="@/assets/im_icon_10.png" alt="左转" v-else />
						</div>
						<div class="indicator-copy"><div class="indicator-label">左转</div><div class="indicator-value">{{ left == 1 ? '开启' : '关闭' }}</div></div>
					</div>
					<div class="indicator-item" :class="{ 'active': right == 1 }">
						<div class="indicator-icon">
							<img class="icon" src="@/assets/im_icon_11.png" alt="右转" v-if="right == 1" />
							<img class="icon" src="@/assets/im_icon_12.png" alt="右转" v-else />
						</div>
						<div class="indicator-copy"><div class="indicator-label">右转</div><div class="indicator-value">{{ right == 1 ? '开启' : '关闭' }}</div></div>
					</div>
					<div class="indicator-item" :class="{ 'active': Brake == 1 }">
						<div class="indicator-icon">
							<img class="icon" src="@/assets/im_icon_07.png" alt="刹车" v-if="Brake == 1" />
							<img class="icon" src="@/assets/im_icon_08.png" alt="刹车" v-else />
						</div>
						<div class="indicator-copy"><div class="indicator-label">刹车</div><div class="indicator-value">{{ Brake == 1 ? '踩下' : '未踩' }}</div></div>
					</div>
					<div class="indicator-item" :class="{ 'active': Clutch == 1 }">
						<div class="indicator-icon">
							<img class="icon" src="@/assets/im_icon_05.png" alt="离合" v-if="Clutch == 1" />
							<img class="icon" src="@/assets/im_icon_06.png" alt="离合" v-else />
						</div>
						<div class="indicator-copy"><div class="indicator-label">离合</div><div class="indicator-value">{{ Clutch == 1 ? '踩下' : '未踩' }}</div></div>
					</div>
					<div class="indicator-item" :class="{ 'active': HandParking == 1 }">
						<div class="indicator-icon">
							<img class="icon" src="@/assets/im_icon_01.png" alt="手刹" v-if="HandParking == 1" />
							<img class="icon" src="@/assets/im_icon_02.png" alt="手刹" v-else />
						</div>
						<div class="indicator-copy"><div class="indicator-label">手刹</div><div class="indicator-value">{{ HandParking == 1 ? '拉起' : '放下' }}</div></div>
					</div>
					<div class="indicator-item" :class="{ 'active': LifeBelt == 1 }">
						<div class="indicator-icon">
							<img class="icon" src="@/assets/im_icon_03.png" alt="安全带" v-if="LifeBelt == 1" />
							<img class="icon" src="@/assets/im_icon_04.png" alt="安全带" v-else />
						</div>
						<div class="indicator-copy"><div class="indicator-label">安全带</div><div class="indicator-value">{{ LifeBelt == 1 ? '已系' : '未系' }}</div></div>
					</div>
					<div class="indicator-item " :class="{ 'active': gear != 0 }">
						<div class="indicator-icon ">
							<div class="text">{{ gear }}</div>
						</div>
						<div class="indicator-copy"><div class="indicator-label">档位</div><div class="indicator-value">当前 {{ gear || '-' }}</div></div>
					</div>
					<div class="indicator-item " :class="{ 'active': gear != 0 }">
						<div class="indicator-icon ">
							<div class="text">{{ rmp }}</div>
						</div>
						<div class="indicator-copy"><div class="indicator-label">转速</div><div class="indicator-value">{{ rmp }} RPM</div></div>
					</div>
				</div>

			</div>
		</footer>
	</div>
</template>

<script>
import {
	getMap
} from '@/utils/map.js'
import {
	getCar
} from '@/utils/car.js'
import EventBus from '@/utils/eventBus'
import Player from './Player.vue'
export default {
	name: 'Trajectory',
	components: {
		Player
	},
	data() {
		return {
			canvasWidth: 0,
			canvasHeight: 0,
			carPosition: {
				x: 0,
				y: 0
			},
			carAngle: 0,
			mapOffset: {
				x: 0,
				y: 0
			},
			initialPosition: null,
			currentPosition: {
				x: 0,
				y: 0
			},
			hasReceivedFirstData: false,
			carModel: [],
			trackPoints: [],
			mapData: [],
			playbackTimer: null,
			currentIndex: 0,
			playbackSpeed: 300,
			playbackSpeeds: [{
				label: '0.5',
				value: 600
			},
			{
				label: '1',
				value: 300
			},
			{
				label: '1.5',
				value: 200
			},
			{
				label: '2',
				value: 150
			}
			],
			playbackProgress: 0,
			isPlaying: false,
			totalDuration: 0,

			scale: 10,
			carImage: null,
			info: {},
			mapList: {},
			curGpsPathList: [],
			speed: 0,
			gear: 0,
			left: 0,
			right: 0,
			CarNum: "",
			carId: 249,
			schoolName: "",
			Brake: 0,
			Clutch: 0,
			HandParking: 0,
			Engine: 0,
			LifeBelt: 0,
			rmp: 0,
			RotateSpeed: 0,
			stuInfo: {},
			videoUrl: '',
			carStatus: false,
			accessToken: '',
			mapFile: '',
			canvasDpr: 1,
			resizeObserver: null,
			canvasResizeFrame: null
		}
	},
	created() {
		//从URL参数中获取carId，支持格式：http://xxx/carId=249 或 http://xxx/?carId=249
		const url = window.location.href;
		let carIdFromUrl = null;

		//尝试从URL中解析carId参数
		const match = url.match(/[?&/]carId=(\d+)/);
		if (match && match[1]) {
			carIdFromUrl = parseInt(match[1]);
		}

		//使用URL参数或默认值
		this.carId = carIdFromUrl || 0;
		console.log('carId:', this.carId);

		setTimeout(() => {
			EventBus.$emit('carIdUpdate', this.carId);
			this.getVehicleData();
		}, 2000)

		EventBus.$emit('carStatus', false);

		this.mapData = this.parseMergedMapData(getMap())
		this.carModel = this.parseMergedCar(getCar())

		this.loadCarImage()
	},
		mounted() {
			this.observeCanvasSize()
			this.initTrackPoints()
			this.initDefaultPosition()
			this.onWindowResize()
			window.addEventListener('resize', this.onWindowResize)
			EventBus.$on('vehicleDataUpdate', this.handleVehicleDataUpdate)
	},
		beforeDestroy() {
			window.removeEventListener('resize', this.onWindowResize)
			if (this.canvasResizeFrame) {
				cancelAnimationFrame(this.canvasResizeFrame)
				this.canvasResizeFrame = null
			}
			if (this.resizeObserver) {
				this.resizeObserver.disconnect()
				this.resizeObserver = null
			}
			EventBus.$off('vehicleDataUpdate', this.handleVehicleDataUpdate)
	},
	methods: {
		initDefaultPosition() {
			if (this.curGpsPathList.length > 0) {
				const firstPoint = this.curGpsPathList[0]
				this.initialPosition = {
					x: firstPoint.x,
					y: firstPoint.y
				}
				this.currentPosition = {
					x: firstPoint.x,
					y: firstPoint.y
				}
				this.mapOffset = {
					x: 0,
					y: 0
				}
			} else if (this.mapData && this.mapData.length > 0) {
				let minX = Infinity,
					maxX = -Infinity,
					minY = Infinity,
					maxY = -Infinity
				this.mapData.forEach(polygon => {
					polygon.forEach(point => {
						minX = Math.min(minX, point[0])
						maxX = Math.max(maxX, point[0])
						minY = Math.min(minY, point[1])
						maxY = Math.max(maxY, point[1])
					})
				})
				const centerX = (minX + maxX) / 2
				const centerY = (minY + maxY) / 2
				this.initialPosition = {
					x: centerX,
					y: centerY
				}
				this.currentPosition = {
					x: centerX,
					y: centerY
				}
				this.mapOffset = {
					x: 0,
					y: 0
				}
			}
			this.carPosition = {
				x: this.canvasWidth / 2,
				y: this.canvasHeight / 2
			}
		},
		//请求接口获取车辆数据
		getVehicleData() {
			console.log("获取车辆数据" + this.carId)
			var that = this;
			// 调用接口获取车辆数据
			this.$http.get('https://dzjltest.xihaxueche.com/mp/app-student/index/carInfo', {
				params: {
					carId: that.carId
				}
			}).then(response => {
				console.log(response)
				if (response.data.code === 200) {
					that.stuInfo = response.data.data.practiseScanBo
					that.CarNum = response.data.data.carNum;
					that.schoolName = response.data.data.schoolName;
					that.accessToken = response.data.data.accessToken;
					that.videoUrl = response.data.data.videoUrl;
					EventBus.$emit('carStatus', true);
					EventBus.$emit('videoInfo', {
						accessToken: that.accessToken,
						videoUrl: that.videoUrl
					});
					this.mapFile = response.data.data.xhSchoolSiteVo.mapFile;
					//下载场地地图
					that.downloadMap(this.mapFile)

				} else {
					//提示车辆不在线
					EventBus.$emit('carStatus', false);
					that.showToast('车辆不在线，请先扫码练车')
				}
			}).catch(error => {
				//提示车辆不在线
				EventBus.$emit('carStatus', false);
				this.showToast('车辆不在线，请先扫码练车')
			})
		},
		downloadMap(url) {
			fetch(url)
				.then(response => response.text())
				.then(data => {
					this.mapData = this.parseMergedMapData(data)
					this.initDefaultPosition()
					this.drawScene()
				})
				.catch(error => {
					this.showToast('地图下载失败')
				})
		},
		onWindowResize() {
			if (this.canvasResizeFrame) return
			this.canvasResizeFrame = requestAnimationFrame(() => {
				this.canvasResizeFrame = null
				if (this.getCanvasSize()) this.drawScene()
			})
		},
		handleVehicleDataUpdate(data) {
			if (!data) return
			this.speed = data.speed || 0
			this.gear = data.gear || '-'
			this.left = data.leftLight || 0
			this.right = data.rightLight || 0
			this.Brake = data.break || 0
			this.Clutch = data.cluth || 0
			this.HandParking = data.handBreak || 0
			this.Engine = data.oil || 0
			this.LifeBelt = data.belt || 0
			this.rmp = data.rmp || 0
			this.CarNum = this.CarNum
			if (data.x !== undefined && data.y !== undefined) {
				this.currentPosition = {
					y: data.x,
					x: data.y
				}
			}
			if (data.azimuth !== undefined) {
				this.carAngle = this.normalizeAngle(data.azimuth - 55)
			}
			this.drawScene()
		},
		getUrlParams() {
			const params = {}
			const url = window.location.search
			if (url) {
				const paramStr = url.slice(1)
				const paramArr = paramStr.split('&')
				paramArr.forEach(item => {
					const [key, value] = item.split('=')
					params[key] = value
				})
			}
			return params
		},
		showToast(message) {
			if (typeof uni !== 'undefined') {
				uni.showToast({
					title: message,
					icon: 'none'
				});
			} else {
				alert(message);
			}
		},
		formatSpeed(speed) {
			if (speed == null || speed === '') return '0'
			return Number(speed).toFixed(1)
		},
		slowPlayback() {
			if (this.playbackSpeed == 1000) {
				this.showToast("已经是最慢速度了")
				return
			}
			this.playbackSpeed += 100
			this.showToast(this.playbackSpeed + "ms")
		},
		fastPlayback() {
			if (this.playbackSpeed == 100) {
				this.showToast('已经是最快速度了')
				return
			}
			this.playbackSpeed -= 100
			this.showToast(this.playbackSpeed + "ms")
		},
		parseMergedCar(txtData) {
			const lines = txtData.split('\n').filter(line => line.trim() !== '')
			const result = []
			lines.forEach(line => {
				const parts = line.split(',')
				if (parts.length < 70) return
				result.push([parseFloat(parts[69]), parseFloat(parts[70]), parseFloat(parts[60])])
			})
			return result
		},
		parseMergedData(txtData) {
			const lines = txtData.split('\n').filter(line => line.trim() !== '')
			const result = []
			lines.forEach(line => {
				const parts = line.split(',')
				if (parts.length < 70) return
				result.push([parseFloat(parts[69]), parseFloat(parts[70]), parseFloat(parts[60])])
			})
			return result
		},
		parseMergedMapData(txtData) {
			const loading = document.createElement('div')
			loading.className = 'custom-loading'
			loading.innerHTML = '加载地图中...'
			document.body.appendChild(loading)
			const lines = txtData.split('\n').filter(line => line.trim() !== '')
			const tempGroups = {}
			lines.forEach(line => {
				const parts = line.split(',')
				if (parts.length < 5) return
				const head = parts[0]
				const lng = parseFloat(parts[2])
				const lat = parseFloat(parts[1])
				const endFlag = parts[parts.length - 1].trim()
				let groupKey = endFlag
				if (endFlag.startsWith("13001")) {
					groupKey = `13001_${head}`
				}
				if (endFlag.startsWith("13002")) {
					groupKey = `13002_${head}`
				}
				if (endFlag.startsWith("13003")) {
					groupKey = `13003_${head}`
				}
				if (endFlag.startsWith("13004")) {
					groupKey = `13004_${head}`
				}
				if (!tempGroups[groupKey]) {
					tempGroups[groupKey] = []
				}
				tempGroups[groupKey].push([lng, lat])
			})
			const result = []
			const validPrefixes = ["11", "12", "13", "14", "15"]
			for (const key in tempGroups) {
				const originalFlag = key.includes("_") ? "13" : key
				const isValid = validPrefixes.some(prefix => originalFlag.startsWith(prefix))
				if (isValid && tempGroups[key].length > 0) {
					result.push(tempGroups[key])
				}
			}
			loading.remove()
			return result
		},
		loadCarImage() {
			const img = new Image()
			img.src = require('@/assets/car.png')
			img.onload = () => {
				this.carImage = img
				this.$nextTick(() => {
					this.drawScene()
				})
			}
			img.onerror = () => {
				console.error('Failed to load car image')
				this.carImage = null
			}
		},
		getCanvasSize() {
			const canvas = this.$refs.trackCanvas
			if (!canvas) return false
			const rect = canvas.getBoundingClientRect()
			if (rect.width <= 0 || rect.height <= 0) return false

			this.canvasWidth = rect.width
			this.canvasHeight = rect.height
			this.canvasDpr = Math.min(window.devicePixelRatio || 1, 3)
			const pixelWidth = Math.max(1, Math.round(rect.width * this.canvasDpr))
			const pixelHeight = Math.max(1, Math.round(rect.height * this.canvasDpr))
			if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
				canvas.width = pixelWidth
				canvas.height = pixelHeight
			}

			const ctx = canvas.getContext('2d')
			if (ctx) {
				ctx.setTransform(this.canvasDpr, 0, 0, this.canvasDpr, 0, 0)
				ctx.imageSmoothingEnabled = true
				if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high'
			}
			this.carPosition = {
				x: this.canvasWidth / 2,
				y: this.canvasHeight / 2
			}
			return true
		},
		observeCanvasSize() {
			const canvas = this.$refs.trackCanvas
			const wrapper = canvas && canvas.parentElement
			if (typeof ResizeObserver !== 'undefined' && wrapper) {
				this.resizeObserver = new ResizeObserver(() => this.onWindowResize())
				this.resizeObserver.observe(wrapper)
			}
		},
		initTrackPoints() {
			this.trackPoints = this.curGpsPathList.map(point => ({
				x: point.x,
				y: point.y
			}))
		},
		drawScene() {
			const canvas = this.$refs.trackCanvas
			if (!canvas) return
			const ctx = canvas.getContext('2d')
			if (!ctx) return
			ctx.setTransform(this.canvasDpr, 0, 0, this.canvasDpr, 0, 0)
			ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
			ctx.save()
			ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2)
			ctx.translate(-(this.currentPosition.x + this.mapOffset.x) * this.scale, -(this.currentPosition.y + this
				.mapOffset.y) * this.scale)
			ctx.scale(this.scale, this.scale)
			this.drawMap(ctx)
			this.drawTrack(ctx)
			ctx.restore()
			this.drawCar(ctx)
		},
		drawMap(ctx) {
			if (!this.mapData || this.mapData.length === 0) return
			this.mapData.forEach(polygon => {
				if (!polygon || polygon.length < 3) return
				ctx.beginPath()
				ctx.moveTo(polygon[0][0], polygon[0][1])
				for (let i = 1; i < polygon.length; i++) {
					const prev = polygon[i - 1]
					const curr = polygon[i]
					const threshold = 500 / this.scale
					if (Math.abs(curr[0] - prev[0]) > threshold || Math.abs(curr[1] - prev[1]) > threshold) {
						ctx.moveTo(curr[0], curr[1])
					} else {
						ctx.lineTo(curr[0], curr[1])
					}
				}
				if (this.shouldClosePolygon(polygon)) {
					ctx.closePath()
				}
				ctx.strokeStyle = '#FFFFFF'
				ctx.lineWidth = 3 / this.scale
				ctx.stroke()
			})
		},
		shouldClosePolygon(points) {
			if (points.length < 3 || points.length > 30) return false
			return true
		},
		drawTrack(ctx) {
			if (!this.trackPoints || this.trackPoints.length < 2) return
			ctx.beginPath()
			ctx.moveTo(this.trackPoints[0].x, this.trackPoints[0].y)
			for (let i = 1; i <= this.currentIndex; i++) {
				ctx.lineTo(this.trackPoints[i].x, this.trackPoints[i].y)
			}
			ctx.strokeStyle = '#35d39a'
			ctx.lineWidth = 3 / this.scale
			ctx.stroke()
		},
		drawCar(ctx) {
			if (this.carImage) {
				ctx.save()
				ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2)
				ctx.rotate((this.carAngle * Math.PI) / 180 + Math.PI + 100)
				const imageWidth = 25
				const imageHeight = 45
				ctx.drawImage(this.carImage, -imageWidth / 2, (-imageHeight / 2) - 14, imageWidth, imageHeight)
				ctx.restore()
			} else {
				ctx.save()
				ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2)
				ctx.rotate((this.carAngle * Math.PI) / 180 + Math.PI)
				this.drawCarFromPoints(ctx)
				ctx.beginPath()
				ctx.moveTo(0, 0)
				ctx.lineTo(1, 0)
				ctx.strokeStyle = '#FFFFFF'
				ctx.lineWidth = 1
				ctx.stroke()
				ctx.restore()
			}
			ctx.save()
			ctx.translate(this.canvasWidth / 2, this.canvasHeight / 2)
			ctx.font = 'bold 11px Arial'
			ctx.textAlign = 'center'
			ctx.textBaseline = 'middle'
			const plateText = this.CarNum
			const plateWidth = ctx.measureText(plateText).width + 4
			const plateHeight = 12
			ctx.fillStyle = '#FFFFFF'
			ctx.fillRect(-plateWidth / 2, -plateHeight / 2, plateWidth, plateHeight)
			ctx.fillStyle = '#168d68'
			ctx.fillText(plateText, 0, 0)
			ctx.restore()
		},
		drawCarFromPoints(ctx) {
			const points = this.parseCarData()
			const scale = 10
			ctx.beginPath()
			points.forEach(([x, y], index) => {
				const scaledX = x * scale
				const scaledY = y * scale
				if (index === 0) {
					ctx.moveTo(scaledX, scaledY)
				} else {
					ctx.lineTo(scaledX, scaledY)
				}
			})
			ctx.closePath()
			ctx.fillStyle = 'rgba(0, 0, 0, 0)'
			ctx.strokeStyle = '#FFFFFF'
			ctx.lineWidth = 2
			ctx.fill()
			ctx.stroke()
		},
		parseCarData() {
			return getCar()
				.trim()
				.split('\n')
				.map(line => {
					const [x, y] = line.split(',').map(Number)
					return [x, y]
				})
				.filter(([x, y]) => !isNaN(x) && !isNaN(y))
		},
		updateCarPosition(index) {
			if (index < 0 || index >= this.curGpsPathList.length) return
			const data = this.curGpsPathList[index]
			this.carAngle = this.normalizeAngle(data.azimuth - 55)
			console.log(this.carAngle)
			this.currentPosition = {
				x: data.x,
				y: data.y
			}
			this.drawScene()
			this.dealSign(data)
		},
		dealSign(data) {
			this.speed = data.speed
			this.gear = data.gear
			this.left = data.leftLight
			this.right = data.rightLight
			this.Brake = data.break
			this.Clutch = data.cluth
			this.HandParking = data.handBreak
			this.Engine = data.oil
			this.LifeBelt = data.belt
			this.rmp = data.rmp
		},
		normalizeAngle(angle) {
			const normalized = angle % 360.0
			if (Math.abs(normalized - 360.0) < 1e-10) {
				return 0.0
			}
			return normalized < 0 ? normalized + 360.0 : normalized
		},
		resetPlayback() {
			this.currentIndex = 0
			this.updateCarPosition(0)
			this.playbackProgress = 0
		},
		goBack() {
			window.history.back()
		},
		updateProgress() {
			this.playbackProgress = Math.floor(
				(this.currentIndex / (this.curGpsPathList.length - 1)) * 100
			)
		},
		onProgressChange(e) {
			const progress = e.target.value
			const targetIndex = Math.floor(
				(progress / 100) * (this.curGpsPathList.length - 1)
			)
			this.currentIndex = targetIndex
			this.updateCarPosition(targetIndex)
			this.playbackProgress = progress
		},
		formatProgress(progress) {
			return `${Math.round(progress)}%`
		},
		onProgressChanging(e) {
			this.playbackProgress = e.target.value
		},
		setPlaybackSpeed(speed) {
			this.playbackSpeed = speed
			if (this.isPlaying) {
				this.startPlayback()
			}
		},
		formatTime(index) {
			if (!this.curGpsPathList || this.curGpsPathList.length === 0) {
				return '00:00'
			}
			const totalSeconds = Math.floor((index / (this.curGpsPathList.length - 1)) * 120)
			const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0')
			const seconds = (totalSeconds % 60).toString().padStart(2, '0')
			return `${minutes}:${seconds}`
		},
		zoomIn() {
			this.scale *= 1.2
			this.drawScene()
		},
		zoomOut() {
			this.scale /= 1.2
			this.drawScene()
		}
	}
}
</script>

<style scoped>
.app-wrapper {
	--cockpit-bg: #0b111c;
	--cockpit-header: #111827;
	--cockpit-surface: #182231;
	--cockpit-surface-raised: #202c3b;
	--cockpit-border: rgba(255, 255, 255, 0.1);
	--cockpit-border-strong: rgba(53, 211, 154, 0.72);
	--cockpit-text: #f5f7fb;
	--cockpit-muted: #9ba8ba;
	--cockpit-accent: #69e49f;
	--cockpit-brand: #35d39a;
	--cockpit-danger: #ff7b86;
}

* {
	box-sizing: border-box;
}

.app-wrapper {
	position: relative;
	width: 100vw;
	height: 100vh;
	min-height: 0;
	height: 100dvh;
	overflow: hidden;
	background: var(--cockpit-bg);
	display: flex;
	flex-direction: column;
	color: var(--cockpit-text);
	font-family: 'Fira Sans', 'PingFang SC', 'Microsoft YaHei', Arial, sans-serif;
}

.header {
	position: relative;
	z-index: 20;
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex: 0 0 58px;
	padding: 8px 12px;
	background: var(--cockpit-header);
	border-bottom: 1px solid var(--cockpit-border);
}

.header-left,
.header-center,
.header-right {
	flex: 1;
	min-width: 0;
}

.header-center {
	text-align: center;
}

.header-right {
	text-align: right;
}

.school-badge,
.student-info,
.car-plate {
	display: inline-flex;
	align-items: center;
}

.school-badge {
	gap: 8px;
	max-width: 150px;
	padding: 5px 10px 5px 6px;
	background: rgba(255, 255, 255, 0.06);
	border: 1px solid rgba(255, 255, 255, 0.08);
	border-radius: 12px;
}

.badge-mark {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 26px;
	height: 26px;
	flex: 0 0 26px;
	background: var(--cockpit-brand);
	border-radius: 8px;
	color: #fff;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: -0.3px;
}

.badge-copy {
	display: flex;
	flex-direction: column;
	min-width: 0;
	text-align: left;
}

.badge-eyebrow {
	color: var(--cockpit-muted);
	font-size: 9px;
	line-height: 12px;
}

.badge-text {
	overflow: hidden;
	color: var(--cockpit-text);
	font-size: 11px;
	font-weight: 600;
	line-height: 14px;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.car-plate {
	gap: 7px;
	padding: 8px 13px;
	background: var(--cockpit-brand);
	border-radius: 9px;
	box-shadow: 0 6px 18px rgba(53, 211, 154, 0.24);
	color: #fff;
	font-family: 'Fira Code', 'Courier New', monospace;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: 1px;
}

.plate-dot,
.live-dot {
	display: inline-block;
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: var(--cockpit-accent);
	box-shadow: 0 0 0 3px rgba(105, 228, 159, 0.14);
}

.student-info {
	gap: 7px;
	justify-content: flex-end;
}

.student-name {
	max-width: 58px;
	overflow: hidden;
	color: var(--cockpit-text);
	font-size: 11px;
	font-weight: 600;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.car-type-badge {
	padding: 4px 7px;
	background: rgba(255, 123, 134, 0.18);
	border: 1px solid rgba(255, 123, 134, 0.36);
	border-radius: 7px;
	color: #ff9ca5;
	font-size: 10px;
	font-weight: 800;
}

.map-section {
	position: relative;
	flex: 1 1 auto;
	min-height: 0;
	margin: 8px;
	overflow: hidden;
	background: #2b3338;
	border: 1px solid var(--cockpit-border);
	border-radius: 16px;
	box-shadow: 0 14px 30px rgba(0, 0, 0, 0.22);
}

.canvas-wrapper {
	position: absolute;
	inset: 0;
	overflow: hidden;
	background: #2b3338;
}

.track-canvas {
	display: block;
	width: 100%;
	height: 100%;
	background: #2b3338;
}

.map-toolbar,
.map-label,
.speed-display {
	position: absolute;
	z-index: 3;
}

.map-toolbar {
	top: 12px;
	left: 14px;
	right: 14px;
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
}

.section-kicker {
	display: block;
	color: var(--cockpit-text);
	font-size: 12px;
	font-weight: 700;
	line-height: 16px;
}

.section-caption {
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 3px;
	color: var(--cockpit-muted);
	font-size: 9px;
	line-height: 13px;
}

.map-controls {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 3px;
	background: rgba(11, 17, 28, 0.74);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 9px;
	backdrop-filter: blur(8px);
}

.map-controls button {
	width: 28px;
	height: 28px;
	padding: 0;
	background: transparent;
	border: 0;
	border-radius: 6px;
	color: var(--cockpit-text);
	cursor: pointer;
	font-size: 16px;
	line-height: 28px;
	transition: background-color 180ms ease, color 180ms ease;
}

.map-controls button:hover,
.map-controls button:focus-visible {
	background: rgba(53, 211, 154, 0.22);
	color: #fff;
	outline: none;
}

.map-controls .zoom-reset {
	width: 38px;
	color: var(--cockpit-muted);
	font-size: 9px;
}

.map-label {
	right: 12px;
	bottom: 12px;
	padding: 5px 7px;
	background: rgba(11, 17, 28, 0.68);
	border: 1px solid rgba(255, 255, 255, 0.08);
	border-radius: 6px;
	color: #dbe4ee;
	font-family: 'Fira Code', 'Courier New', monospace;
	font-size: 8px;
	letter-spacing: 0.6px;
}

.speed-display {
	left: 14px;
	bottom: 18px;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 7px 10px 7px 7px;
	background: rgba(16, 24, 36, 0.86);
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 28px;
	box-shadow: 0 8px 18px rgba(0, 0, 0, 0.22);
	backdrop-filter: blur(8px);
}

.speed-ring {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 52px;
	height: 52px;
	background: #d7dbe1;
	border: 3px solid #fff;
	border-radius: 50%;
	color: #111827;
}

.speed-display.warning .speed-ring {
	background: #ff8a93;
	border-color: #ffd4d7;
}

.speed-value {
	font-family: 'Fira Code', 'Courier New', monospace;
	font-size: 15px;
	font-weight: 700;
	line-height: 16px;
}

.speed-unit {
	font-size: 8px;
	line-height: 10px;
}

.speed-caption {
	color: var(--cockpit-muted);
	font-size: 10px;
	white-space: nowrap;
}

.player-section {
	position: relative;
	flex: 0 0 218px;
	margin: 0 8px 8px;
	padding: 10px 10px 8px;
	overflow: hidden;
	background: #151e2b;
	border: 1px solid rgba(105, 228, 159, 0.72);
	border-radius: 16px;
	box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
}

.player-body {
	height: calc(100% - 36px);
	min-height: 0;
}

.player-body >>> .ezuikit-js,
.player-body >>> .video-wrapper {
	height: 100%;
	min-height: 0;
}

.player-heading {
	position: relative;
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: 29px;
	margin: 0 2px 7px;
}

.video-status,
.status-pulse {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 5px 7px;
	background: rgba(105, 228, 159, 0.1);
	border: 1px solid rgba(105, 228, 159, 0.22);
	border-radius: 7px;
	color: var(--cockpit-accent);
	font-size: 9px;
}

.player-section::after {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: 3px;
	background: var(--cockpit-accent);
	content: '';
	opacity: 0.9;
}

.footer {
	position: absolute;
	top: 72px;
	left: 14px;
	right: 14px;
	z-index: 10;
	padding: 12px 10px 10px;
	background: rgba(19, 28, 42, 0.58);
	border: 1px solid rgba(255, 255, 255, 0.18);
	border-radius: 16px;
	box-shadow: 0 14px 28px rgba(0, 0, 0, 0.24);
	backdrop-filter: blur(9px) saturate(125%);
}

.status-title {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 2px 9px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.status-title > div {
	display: flex;
	align-items: baseline;
	gap: 7px;
}

.status-title .section-caption {
	display: inline;
	margin: 0;
}

.status-pulse {
	padding: 0;
	background: transparent;
	border: 0;
}

.status-panel {
	padding-top: 9px;
}

.status-indicators {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 6px;
}

.indicator-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 58px;
	padding: 6px 3px;
	background: rgba(255, 255, 255, 0.045);
	border: 1px solid rgba(255, 255, 255, 0.06);
	border-radius: 10px;
	transition: background-color 180ms ease, border-color 180ms ease, color 180ms ease;
}

.indicator-item.active {
	background: rgba(105, 228, 159, 0.12);
	border-color: rgba(105, 228, 159, 0.48);
}

.indicator-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 26px;
	height: 26px;
	margin-bottom: 3px;
	filter: grayscale(100%);
	opacity: 0.58;
	color: #e8edf4;
	transition: filter 180ms ease, opacity 180ms ease;
}

.indicator-icon img {
	width: 26px;
	height: 26px;
	object-fit: contain;
}

.indicator-icon .text {
	font-family: 'Fira Code', 'Courier New', monospace;
	font-size: 16px;
	font-weight: 700;
}

.indicator-item.active .indicator-icon {
	filter: grayscale(0%);
	opacity: 1;
}

.indicator-copy {
	min-width: 0;
	text-align: center;
}

.indicator-label {
	color: #d8e0e9;
	font-size: 10px;
	font-weight: 600;
	line-height: 13px;
}

.indicator-value {
	overflow: hidden;
	margin-top: 1px;
	color: var(--cockpit-muted);
	font-size: 8px;
	line-height: 11px;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.indicator-item.active .indicator-label,
.indicator-item.active .indicator-value {
	color: var(--cockpit-accent);
}

button:focus-visible {
	outline: 2px solid var(--cockpit-brand);
	outline-offset: 2px;
}

@media screen and (min-width: 768px) {
	.app-wrapper {
		padding: 10px 14px;
	}

	.header {
		flex-basis: 64px;
		padding: 8px 16px;
		border: 1px solid var(--cockpit-border);
		border-radius: 14px;
	}

	.map-section {
		margin: 10px 0;
	}

	.player-section {
		align-self: flex-end;
		width: min(460px, 100%);
		flex-basis: 218px;
		margin: 0 0 0;
	}

	.footer {
		top: 88px;
		left: 24px;
		right: auto;
		width: 460px;
	}
}

@media screen and (max-width: 390px) {
	.header {
		padding-left: 9px;
		padding-right: 9px;
	}

	.school-badge {
		max-width: 128px;
		padding-right: 7px;
	}

	.badge-eyebrow {
		display: none;
	}

	.car-plate {
		padding: 8px 9px;
		font-size: 11px;
		letter-spacing: 0.5px;
	}

	.student-name {
		max-width: 42px;
	}

	.footer {
		top: 66px;
		left: 10px;
		right: 10px;
		padding-left: 8px;
		padding-right: 8px;
	}

	.status-indicators {
		gap: 5px;
	}

	.indicator-item {
		min-height: 55px;
	}

	.indicator-icon,
	.indicator-icon img {
		width: 23px;
		height: 23px;
	}

	.indicator-label {
		font-size: 9px;
	}

	.indicator-value {
		font-size: 7px;
	}

	.player-section {
		flex-basis: 218px;
	}
}

@media screen and (max-height: 680px) and (max-width: 767px) {
	.player-section {
		flex-basis: 190px;
	}

	.footer {
		top: 62px;
		padding-top: 8px;
		padding-bottom: 7px;
	}

	.status-title {
		padding-bottom: 6px;
	}

	.status-panel {
		padding-top: 6px;
	}

	.indicator-item {
		min-height: 48px;
		padding-top: 3px;
		padding-bottom: 3px;
	}

	.indicator-icon,
	.indicator-icon img {
		width: 20px;
		height: 20px;
	}
}

@media (prefers-reduced-motion: reduce) {
	*,
	*::before,
	*::after {
		animation-duration: 0.01ms !important;
		transition-duration: 0.01ms !important;
	}
}
</style>
